import { Link, useLocation, useNavigate } from "react-router-dom";
import { Crown, KanbanSquare, Users, Settings, LogOut, Menu, X, BarChart3, Mail } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { ReplyAlerts } from "./ReplyAlerts";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = [
    { to: "/app", label: "Pipeline", icon: KanbanSquare },
    { to: "/app/outreach", label: "Outreach", icon: Mail },
    { to: "/app/reports", label: "Reports", icon: BarChart3 },
    ...(user?.role === "admin" ? [{ to: "/app/team", label: "Team", icon: Users }] : []),
    { to: "/app/settings", label: "Settings", icon: Settings },
  ];

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-5 py-6 border-b border-slate-800">
        <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center">
          <Crown className="w-5 h-5 text-black" />
        </div>
        <div className="leading-tight">
          <p className="font-display font-extrabold tracking-tight">King of IT</p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-amber-500/80">CRM</p>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {nav.map((n) => {
          const active = location.pathname === n.to;
          return (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              <n.icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
              {n.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="px-4 py-2 mb-2">
          <p className="text-sm font-semibold truncate">{user?.name}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          {user?.role === "admin" && (
            <span className="inline-block mt-1 text-[10px] uppercase tracking-wider text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
              Admin
            </span>
          )}
        </div>
        <button
          data-testid="logout-button"
          onClick={doLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B1120] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-slate-800 bg-[#0B1120] flex-col fixed inset-y-0">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-[#0B1120] border-r border-slate-800">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg gold-gradient flex items-center justify-center">
              <Crown className="w-4 h-4 text-black" />
            </div>
            <span className="font-display font-bold">King of IT CRM</span>
          </div>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="text-slate-300">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </header>
        {children}
      </div>
      <ReplyAlerts />
    </div>
  );
}
